import {
  Assessment,
  BusinessUnit,
  Component,
  QualificationMin,
  Section,
  Series,
  EntryComponent,
  ReplacementFilesComponentDocumentsDto,
  SectionBase,
  SelectedBulkFilesDownloadBody,
  SortOrder,
} from '@cambridgeassessment/lib-ee-commons';
import { DownloadData, DownloadUrl, WorkDownloadingParams } from '../../types/Sections';
import { get } from '../httpHelpers/submitter';
import * as papiEntree from '../httpHelpers/papiEntree';
import { PagedFetchResult } from '../httpHelpers/pagedFetch';
import * as uploader from '../httpHelpers/uploader';
import qs from 'qs';
import { getToken } from '../../util/getToken';
import { GetSubmittedComponentDocumentsApiParams } from '@cambridgeassessment/lib-ee-commons/src/domain/external/dto/submitter';

export const getTableData = async (
  businessUnitId: string,
  {
    seriesId,
    assessmentCode,
    componentNumber,
    qualificationId,
    sectionId,
    centreId,
    learnerNumber,
    isComponentDocument,
  }: WorkDownloadingParams,
  nextUrl?: string,
): Promise<PagedFetchResult<DownloadData>> => {
  const params = qs.stringify({ sectionId, centreId, learnerNumber, isComponentDocument });
  const url = `v1/businessUnit/${businessUnitId}/series/${seriesId}/${assessmentCode}/${componentNumber}/${qualificationId}/tableData?${params}`;

  return papiEntree.pagedFetch(url, nextUrl, { pageSize: 15 });
};

export const getSubmittedQualifications = (
  businessUnitId: BusinessUnit['id'],
  seriesId: Series['id'],
): Promise<QualificationMin[]> => {
  const params = qs.stringify({ fieldName: 'name', order: SortOrder.ASC });
  return get<QualificationMin[]>(
    `v1/businessUnit/${businessUnitId}/series/${seriesId}/submittedQualifications?${params}`,
  );
};

export const getSubmittedAssessments = (
  businessUnitId: BusinessUnit['id'],
  seriesId: Series['id'],
  qualificationId: QualificationMin['id'],
): Promise<Assessment[]> => {
  const params = qs.stringify({ fieldName: 'code', order: SortOrder.ASC });
  return get<Assessment[]>(
    `v1/businessUnit/${businessUnitId}/series/${seriesId}/qualification/${qualificationId}/submittedAssessments?${params}`,
  );
};

export const getSubmittedComponents = (
  businessUnitId: BusinessUnit['id'],
  seriesId: Series['id'],
  qualificationId: QualificationMin['id'],
  assessmentCode: Assessment['code'],
): Promise<EntryComponent[]> => {
  return get<EntryComponent[]>(
    `v1/businessUnit/${businessUnitId}/series/${seriesId}/qualification/${qualificationId}/assessment/${assessmentCode}/submittedComponents`,
  );
};

export const getSubmittedSections = (
  businessUnitId: BusinessUnit['id'],
  seriesId: Series['id'],
  qualificationId: QualificationMin['id'],
  assessmentCode: Assessment['code'],
  componentNumber: Component['number'],
): Promise<SectionBase[]> => {
  return get<SectionBase[]>(
    `v1/businessUnit/${businessUnitId}/series/${seriesId}/qualification/${qualificationId}/assessment/${assessmentCode}/component/${componentNumber}/submittedSections`,
  );
};

export const getDownloadLink = (
  businessUnitId: BusinessUnit['id'],
  id: Section['_id'],
  dispositionType: string,
  uniqId: string,
  originalFilename?: string,
  allowMultipleFiles?: boolean,
  isComponentDocument?: boolean,
): Promise<DownloadUrl> => {
  const params = qs.stringify({ dispositionType, uniqId, originalFilename, allowMultipleFiles, isComponentDocument });

  return papiEntree.get(`v1/businessUnit/${businessUnitId}/sections/download-link/${id}?${params}`);
};

export const getSectionDownloadZipLink = (
  businessUnitId: string,
  centreId: string,
  seriesId: number,
  assessmentCode: string,
  componentNumber: string,
  learnerNumber: string,
  learnerUniqueId: string,
  seriesName?: string,
): string => {
  const token = `Bearer ${getToken()}`;
  const params = {
    auth: token,
    seriesName,
  };

  return `${
    uploader.uploaderUrl
  }/v1/businessUnit/${businessUnitId}/files/${centreId}/${seriesId}/${assessmentCode}/${componentNumber}/${learnerNumber}/${learnerUniqueId}/download-additional-files?${qs.stringify(
    params,
  )}`;
};

export const getArchiveOfAllFiles = (businessUnitId: string, params: WorkDownloadingParams): Promise<Blob> =>
  papiEntree.post(`v1/businessUnit/${businessUnitId}/files`, params);

export const getSelectedFilesArchive = (businessUnitId: string, body: SelectedBulkFilesDownloadBody): Promise<Blob> =>
  uploader.post(`v1/businessUnit/${businessUnitId}/files/selected`, body);

export const getSubmittedComponentDocuments = ({
  businessUnitId,
  centreId,
  seriesId,
  assessmentCode,
  componentNumber,
  sortBy,
  order,
}: GetSubmittedComponentDocumentsApiParams): Promise<ReplacementFilesComponentDocumentsDto[]> => {
  const path = `v1/businessUnit/${businessUnitId}/centres/${centreId}/series/${seriesId}/assessments/${assessmentCode}/components/${componentNumber}/submitted-component-documents`;
  const queryParams = [];
  if (sortBy) {
    queryParams.push(`sortBy=${encodeURIComponent(sortBy)}`);
  }

  if (order) {
    queryParams.push(`order=${encodeURIComponent(order)}`);
  }

  if (queryParams.length > 0) {
    const pathWithQuery = `${path}?${queryParams.join('&')}`;
    return get(pathWithQuery);
  }

  return get(path);
};
